import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Modalità kiosk / totem (decisione 16).
 * ---------------------------------------------------------------------------
 * È una **modalità della stessa SPA**, non un sito separato. Il portale
 * statico aveva quattro rese indipendenti (`/`, `/m/`, `/kh/`, `/kv/`) con
 * layout e CSS separati; `/m/` e `/scegli/` sono eliminati.
 *
 * Costanti conservate identiche al totem attuale (`kiosk/kiosk-totem.js`),
 * perché sono state tarate sull'uso reale:
 *   IDLE_MS       = 90_000   -> 90 s: compare l'invito "tocca per iniziare"
 *   IDLE_RESET_MS = 240_000  -> 4 min: ritorno alla home e reset dello stato
 *
 * Il fullscreen NON si può richiedere all'avvio: i browser lo consentono solo
 * **dentro il gesto dell'utente**. Per questo `requestFullscreen()` è esposto
 * e va chiamato da un `onClick`, non da un `useEffect`. È il vincolo che
 * rendeva necessario il `launcher.html` nel portale statico.
 */

const IDLE_MS = 90_000;
const IDLE_RESET_MS = 240_000;
const STORAGE_KEY = 'ic-kiosk-mode';

interface KioskContextValue {
  readonly enabled: boolean;
  readonly attractVisible: boolean;
  /** Da chiamare DENTRO un handler di click: altrimenti il browser rifiuta. */
  readonly enterKiosk: () => Promise<void>;
  readonly exitKiosk: () => Promise<void>;
  readonly dismissAttract: () => void;
}

const KioskContext = createContext<KioskContextValue | null>(null);

function readEnabled(search: string): boolean {
  // `?kiosk=1` arma la modalità (usato dai QR e dal launcher del totem).
  const param = new URLSearchParams(search).get('kiosk');
  if (param === '1' || param === 'true') return true;
  if (param === '0' || param === 'false') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function KioskProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(() => readEnabled(window.location.search));
  const [attractVisible, setAttractVisible] = useState(false);
  const idleTimer = useRef<number | undefined>(undefined);
  const resetTimer = useRef<number | undefined>(undefined);

  // La rotta /kiosk/ arma la modalità da sé.
  useEffect(() => {
    if (location.pathname.startsWith('/kiosk')) setEnabled(true);
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle('kiosk-mode', enabled);
    try {
      if (enabled) window.localStorage.setItem(STORAGE_KEY, '1');
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage negato: la modalità resta valida per la sessione */
    }
  }, [enabled]);

  /* ---------------- Attract loop + reset ---------------- */
  const resetTimers = useCallback(() => {
    setAttractVisible(false);
    window.clearTimeout(idleTimer.current);
    window.clearTimeout(resetTimer.current);
    if (!enabled) return;

    idleTimer.current = window.setTimeout(() => setAttractVisible(true), IDLE_MS);
    resetTimer.current = window.setTimeout(() => {
      setAttractVisible(true);
      // Torna alla home: il totem non deve restare sulla scheda di un comune
      // consultata da qualcun altro mezz'ora prima.
      navigate('/', { replace: true });
    }, IDLE_RESET_MS);
  }, [enabled, navigate]);

  useEffect(() => {
    if (!enabled) {
      window.clearTimeout(idleTimer.current);
      window.clearTimeout(resetTimer.current);
      setAttractVisible(false);
      return;
    }

    const events: readonly (keyof DocumentEventMap)[] = [
      'pointerdown',
      'keydown',
      'wheel',
      'touchstart',
    ];
    for (const event of events) {
      document.addEventListener(event, resetTimers, { passive: true });
    }
    resetTimers();

    return () => {
      for (const event of events) document.removeEventListener(event, resetTimers);
      window.clearTimeout(idleTimer.current);
      window.clearTimeout(resetTimer.current);
    };
  }, [enabled, resetTimers]);

  /* ---------------- Blocco dei link esterni ----------------
     Il totem non deve poter uscire dal sito: un browser lasciato aperto su
     un link esterno è un kiosk perso. Intercettato in fase di CAPTURE, così
     nessun handler di componente può aggirarlo. */
  useEffect(() => {
    if (!enabled) return;

    const onClickCapture = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest?.('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;

      const href = anchor.getAttribute('href') ?? '';
      // Ancore interne e rotte relative: consentite.
      if (href.startsWith('#') || href.startsWith('/')) return;

      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }

      const sameOrigin = url.origin === window.location.origin;
      const isWebScheme = url.protocol === 'http:' || url.protocol === 'https:';
      // `tel:` e `mailto:` non aprono un browser navigabile: sono inerti su
      // un totem e non vanno bloccati (potrebbero servire a un operatore).
      if (!isWebScheme) return;
      if (sameOrigin) return;

      event.preventDefault();
      event.stopPropagation();
    };

    document.addEventListener('click', onClickCapture, true);
    return () => document.removeEventListener('click', onClickCapture, true);
  }, [enabled]);

  /* Marca i link esterni per la resa visiva. Rieseguito a ogni rotta. */
  useEffect(() => {
    for (const anchor of document.querySelectorAll<HTMLAnchorElement>('a[href]')) {
      const href = anchor.getAttribute('href') ?? '';
      const external = /^https?:\/\//i.test(href) && !href.startsWith(window.location.origin);
      if (external) anchor.dataset.external = 'true';
    }
  }, [location.pathname, enabled]);

  /* ---------------- Fullscreen ----------------
     DEVE essere invocato dentro il gesto di click. Chiamarlo da un effetto
     produce `NotAllowedError` in tutti i browser. */
  const enterKiosk = useCallback(async () => {
    setEnabled(true);
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
      }
    } catch {
      // Il fullscreen è un miglioramento, non un requisito: se il browser lo
      // rifiuta (o siamo in un iframe) la modalità kiosk resta attiva.
    }
  }, []);

  const exitKiosk = useCallback(async () => {
    setEnabled(false);
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch {
      /* nulla da fare */
    }
  }, []);

  const value = useMemo<KioskContextValue>(
    () => ({
      enabled,
      attractVisible,
      enterKiosk,
      exitKiosk,
      dismissAttract: resetTimers,
    }),
    [attractVisible, enabled, enterKiosk, exitKiosk, resetTimers],
  );

  return <KioskContext.Provider value={value}>{children}</KioskContext.Provider>;
}

export function useKiosk(): KioskContextValue {
  const context = useContext(KioskContext);
  if (!context) throw new Error('useKiosk richiede <KioskProvider>');
  return context;
}

export { IDLE_MS, IDLE_RESET_MS };
